import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getLanguageFromSearch,
  isPublicPath,
  isSupportedLanguage,
  withLanguage,
} from '../utils/languageUrl';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const urlLanguage = getLanguageFromSearch(window.location.search);
    const savedLanguage = localStorage.getItem('language');
    return urlLanguage || (isSupportedLanguage(savedLanguage) ? savedLanguage : 'tr');
  });

  useEffect(() => {
    const urlLanguage = getLanguageFromSearch(location.search);
    const savedLanguage = localStorage.getItem('language');
    const fallbackLanguage =
      isSupportedLanguage(savedLanguage) ? savedLanguage : 'tr';

    if (isPublicPath(location.pathname) && !urlLanguage) {
      setCurrentLanguage(fallbackLanguage);
      i18n.changeLanguage(fallbackLanguage);
      localStorage.setItem('language', fallbackLanguage);
      document.documentElement.lang = fallbackLanguage;
      navigate(
        withLanguage(
          `${location.pathname}${location.search}${location.hash}`,
          fallbackLanguage
        ),
        { replace: true }
      );
      return;
    }

    const nextLanguage = urlLanguage || currentLanguage;
    setCurrentLanguage(nextLanguage);
    i18n.changeLanguage(nextLanguage);
    localStorage.setItem('language', nextLanguage);
    document.documentElement.lang = nextLanguage;
  }, [
    currentLanguage,
    i18n,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!isPublicPath(location.pathname)) {
      document.head
        .querySelectorAll('link[data-krl-seo]')
        .forEach((link) => link.remove());
      return undefined;
    }

    const language =
      getLanguageFromSearch(location.search) || currentLanguage;
    const currentTarget = `${location.pathname}${location.search}`;
    const canonicalUrl = `https://www.krlkimya.com${withLanguage(
      currentTarget,
      language
    )}`;

    const upsertLink = (key, attributes) => {
      let link = document.head.querySelector(`link[data-krl-seo="${key}"]`);
      if (!link) {
        link = document.createElement('link');
        link.dataset.krlSeo = key;
        document.head.appendChild(link);
      }
      Object.entries(attributes).forEach(([name, value]) =>
        link.setAttribute(name, value)
      );
    };

    upsertLink('canonical', { rel: 'canonical', href: canonicalUrl });
    upsertLink('alternate-tr', {
      rel: 'alternate',
      hreflang: 'tr',
      href: `https://www.krlkimya.com${withLanguage(currentTarget, 'tr')}`,
    });
    upsertLink('alternate-en', {
      rel: 'alternate',
      hreflang: 'en',
      href: `https://www.krlkimya.com${withLanguage(currentTarget, 'en')}`,
    });

    return undefined;
  }, [currentLanguage, location.pathname, location.search]);

  const changeLanguage = (language) => {
    if (!isSupportedLanguage(language)) return;
    setCurrentLanguage(language);
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
    
    // Update HTML lang attribute
    document.documentElement.lang = language;
    
    // Update page title based on language
    if (language === 'en') {
      document.title = 'KRL KİMYA';
    } else {
      document.title = 'KRL KİMYA';
    }
  };

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'tr' ? 'en' : 'tr';
    changeLanguage(newLanguage);
    if (isPublicPath(location.pathname)) {
      navigate(
        withLanguage(
          `${location.pathname}${location.search}${location.hash}`,
          newLanguage
        )
      );
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    toggleLanguage,
    isEnglish: currentLanguage === 'en',
    isTurkish: currentLanguage === 'tr'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 